import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Check } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { HeroFX } from "./HeroFX";

const HERO_BG = "https://images.unsplash.com/photo-1762279389042-9439bfb6c155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBmdXR1cmlzdGljJTIwbmVvbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc4NDE3ODEwfDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t, locale } = useI18n();
  const isDE = locale === "de";

  return (
    <section data-testid="hero-section" className="relative min-h-[100vh] flex items-center overflow-hidden pt-20">
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/70 via-[#05050A]/80 to-[#05050A]" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 neural-glow" />
        <HeroFX />
      </div>

      <div className="relative z-10 px-6 md:px-12 lg:px-24 w-full">
        <div className="max-w-5xl">
          <div className="anim-fade-up flex items-center gap-3 mb-8" style={{ animationDelay: "0.1s" }}>
            <span className="w-12 h-px bg-brand-azure" />
            <span className="label-mono text-white/70">{t.hero.kicker}</span>
          </div>

          <h1
            data-testid="hero-title"
            className="anim-fade-up font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.95] text-white"
            style={{ animationDelay: "0.25s" }}
          >
            Automate.{" "}
            <span className="text-brand-orange">Innovate.</span>{" "}
            Elevate.
          </h1>

          <p
            className="anim-fade-up mt-8 max-w-2xl text-lg md:text-xl text-white/65 leading-relaxed"
            style={{ animationDelay: "0.4s" }}
          >
            {t.hero.subtitle}
          </p>

          <div className="anim-fade-up mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "0.55s" }}>
            <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary group">
              {t.hero.cta1}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/services" data-testid="hero-cta-secondary" className="btn-secondary">
              <Sparkles className="w-4 h-4" />
              {t.hero.cta2}
            </Link>
          </div>

          <div
            data-testid="hero-trust"
            className="anim-fade-up mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-white/65"
            style={{ animationDelay: "0.65s" }}
          >
            {(t.hero.trust || []).map((label, i) => (
              <span key={i} className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-brand-orange" strokeWidth={2.6} />
                <span>{label}</span>
              </span>
            ))}
          </div>

          <div className="anim-fade-up mt-14 grid grid-cols-3 gap-6 md:gap-12 max-w-2xl" style={{ animationDelay: "0.8s" }}>
            <Stat label={t.hero.stat1} value="40+" />
            <Stat label={t.hero.stat2} value="<24h" />
            <Stat label={t.hero.stat3} value="92%" />
          </div>
        </div>
      </div>

      {/* Premium animated scroll indicator — mouse-wheel glyph */}
      <a
        href="#digital-ecosystem"
        aria-label={isDE ? "Nach unten scrollen" : "Scroll down"}
        data-testid="hero-scroll-indicator"
        className="hero-scroll absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3 text-white/40 hover:text-brand-orange transition-colors group"
      >
        <span className="label-mono text-[10px] tracking-[0.28em] group-hover:text-brand-orange transition-colors">
          {isDE ? "MEHR ENTDECKEN" : "EXPLORE MORE"}
        </span>
        <span className="relative w-[26px] h-[42px] rounded-full border-[1.5px] border-white/30 group-hover:border-brand-orange/70 transition-colors flex justify-center overflow-hidden">
          <span className="hero-scroll-wheel absolute top-2 w-[3px] h-[7px] rounded-full bg-white/60 group-hover:bg-brand-orange" />
        </span>
        <span aria-hidden className="hero-scroll-chevron flex flex-col gap-[3px] mt-[2px]">
          <span className="w-2 h-2 rotate-45 border-r-[1.5px] border-b-[1.5px] border-white/25 group-hover:border-brand-orange/60 transition-colors" />
          <span className="w-2 h-2 rotate-45 border-r-[1.5px] border-b-[1.5px] border-white/15 group-hover:border-brand-orange/40 transition-colors" />
        </span>
      </a>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div data-testid={`hero-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className="font-display text-3xl md:text-4xl text-white">{value}</div>
    <div className="mt-2 label-mono text-white/40">{label}</div>
  </div>
);
